import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../App";
import secureStorage from "../utils/secureStorage";
import { useNavigate } from "react-router-dom";
import { Users, Baby, Palette, Utensils, FileText, Receipt, DollarSign, Clock, CheckCircle, XCircle, Eye, ExternalLink, RefreshCw, Calendar, Filter, Bell, Phone, ThumbsUp, ThumbsDown, AlertCircle, Trash2, Key, BookOpen, ArrowLeft, Sparkles, Heart, Star, TrendingUp, MessageCircle, GraduationCap, Loader2, KeyRound, Shield, Candy, Smile, IceCreamCone, Gift } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PageLayout } from "../components/layout/Sidebar";
import useFeaturePermissions from "../hooks/useFeaturePermissions";
import PushNotificationPrompt from "../components/notifications/PushNotificationPrompt";
import AdminNotifications from "../components/notifications/AdminNotifications";
import PullToRefresh from "../components/common/PullToRefresh";
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { FloatingElements, AnimatedStatCard, AnimatedHeader, QuickActionButton, GreetingBanner, AnimatedCard, ParentIcon, TeacherIcon } from "../components/ui/AnimatedElements";
import { BirthdayNotificationCard, useBirthdayChildren } from "../components/notifications/BirthdayNotification";
import { useModuleAccess } from "../hooks/useModuleAccess";
import { getLocalDateString } from "../lib/utils";
import AttendanceModule from "../components/admin/AttendanceModule";
import TemaCountdownWidget from "../components/widgets/TemaCountdownWidget";
import BirthdayReminderWidget from "../components/widgets/BirthdayReminderWidget";
// RatioCalculator & FoodMenuReference - hanya untuk Branch Manager Dashboard

export default function AdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { hasAccess } = useFeaturePermissions(user?.role);
  const { birthdayChildren } = useBirthdayChildren(user);
  const { isModuleOn } = useModuleAccess(user?.role);
  const [stats, setStats] = useState({});
  const [billingStats, setBillingStats] = useState({});
  
  // Check if this is an impersonated session
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalManagerName, setOriginalManagerName] = useState("");
  const [hasSuperadminAccess, setHasSuperadminAccess] = useState(false);
  
  useEffect(() => {
    const originalToken = secureStorage.getItem("original_manager_token");
    const originalUser = secureStorage.getItem("original_manager_user");
    const superadminToken = secureStorage.getItem("original_superadmin_token");
    
    if (originalToken && originalUser) {
      setIsImpersonating(true);
      try {
        const userData = JSON.parse(originalUser);
        setOriginalManagerName(userData.name || "Pengurus Cawangan");
      } catch (e) {
        setOriginalManagerName("Pengurus Cawangan");
      }
    }
    
    // ONLY show "Super Admin" button if there's a superadmin token in the chain
    // This means the impersonation chain started from Superadmin
    if (superadminToken) {
      setHasSuperadminAccess(true);
    }
  }, []);
  
  // Handle return to Branch Manager dashboard
  const handleReturnToManager = () => {
    const originalToken = secureStorage.getItem("original_manager_token");
    const originalUser = secureStorage.getItem("original_manager_user");
    
    if (originalToken && originalUser) {
      // Restore original token and user
      secureStorage.setItem("token", originalToken);
      secureStorage.setItem("user", originalUser);
      
      // Clear impersonation data
      secureStorage.removeItem("original_manager_token");
      secureStorage.removeItem("original_manager_user");
      
      toast.success("Kembali ke Dashboard Pengurus Cawangan");
      
      // Reload to apply changes
      window.location.href = "/";
    }
  };
  
  // Handle return directly to Superadmin dashboard
  const handleReturnToSuperadmin = () => {
    const superadminToken = secureStorage.getItem("original_superadmin_token");
    const superadminUser = secureStorage.getItem("original_superadmin_user");
    
    if (superadminToken && superadminUser) {
      // Restore superadmin token and user
      secureStorage.setItem("token", superadminToken);
      secureStorage.setItem("user", superadminUser);
      
      // Clear ALL impersonation data
      secureStorage.removeItem("original_superadmin_token");
      secureStorage.removeItem("original_superadmin_user");
      secureStorage.removeItem("original_manager_token");
      secureStorage.removeItem("original_manager_user");
      
      toast.success("Kembali ke Dashboard Super Admin");
      
      // Reload to apply changes
      window.location.href = "/";
    }
  };
  const [showInvoiceList, setShowInvoiceList] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
  const [pendingParents, setPendingParents] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all, paid, pending, unpaid
  const [sendingReminder, setSendingReminder] = useState(null); // Track which invoice is sending reminder
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [showTeachersModal, setShowTeachersModal] = useState(false);
  const [impersonatingTeacher, setImpersonatingTeacher] = useState(null);
  const [nutritionalSummary, setNutritionalSummary] = useState({
    total_children: 0,
    normal_children: 0,
    malnourished_children: 0,
    no_data_or_out_of_range: 0
  });
  
  // Ratio & Food Menu modules removed - only for Branch Manager

  const fetchStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/stats');
      setStats(response.data);
    } catch (error) {
      toast.error("Ralat mendapatkan statistik");
    }
  }, []);

  const fetchBillingStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/billing/stats');
      setBillingStats(response.data);
    } catch (error) {
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/billing/invoices');
      setInvoices(response.data);
    } catch (error) {
    }
  }, []);

  const fetchPendingParents = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/admin/pending-parents');
      setPendingParents(response.data);
    } catch (error) {
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/admin/notifications');
      setNotifications(response.data || []);
    } catch (error) {
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/admin/notifications/unread-count');
      setUnreadCount(response.data?.count || 0);
    } catch (error) {
    }
  }, []);

  const fetchNutritionalSummary = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/nutrition-status/summary');
      const childrenData = response.data || [];
      
      // Process array to calculate summary statistics
      const total = childrenData.length;
      const normal = childrenData.filter(c => c.bmi_status === 'Normal').length;
      const malnourished = childrenData.filter(c => 
        c.bmi_status === 'Underweight' || c.bmi_status === 'Severely Underweight' ||
        c.bmi_status === 'Kurang Berat' || c.bmi_status === 'Sangat Kurang Berat'
      ).length;
      const noData = childrenData.filter(c => 
        !c.bmi_status || c.bmi_status === 'Not Set' || c.bmi === 0
      ).length;
      
      setNutritionalSummary({
        total_children: total,
        normal_children: normal,
        malnourished_children: malnourished,
        no_data_or_out_of_range: noData
      });
    } catch (error) {
    }
  }, []);

  const fetchTeachers = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/users/teachers');
      setTeachers(response.data || []);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    }
  }, []);

  const handleImpersonateTeacher = async (teacherId, teacherName) => {
    setImpersonatingTeacher(teacherId);
    try {
      const response = await axiosInstance.post(`/users/impersonate-teacher?teacher_id=${encodeURIComponent(teacherId)}`);
      
      if (response.data.token && response.data.user) {
        const currentToken = secureStorage.getItem("token");
        const currentUser = secureStorage.getItem("user");
        secureStorage.setItem("original_manager_token", currentToken);
        secureStorage.setItem("original_manager_user", currentUser);
        secureStorage.setItem("token", response.data.token);
        secureStorage.setItem("user", JSON.stringify(response.data.user));
        
        toast.success(`🎉 Berjaya masuk sebagai Guru ${teacherName}!`);
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Impersonate teacher error:", error);
      toast.error(error.response?.data?.detail || "Gagal masuk sebagai guru ini");
    } finally {
      setImpersonatingTeacher(null);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axiosInstance.put(`/admin/notifications/${notificationId}/read`);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axiosInstance.delete(`/admin/notifications/${notificationId}`);
      toast.success("Notifikasi dipadam");
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      toast.error("Ralat memadam notifikasi");
    }
  };

  const handleApproveParent = async (userId) => {
    try {
      await axiosInstance.post(`/admin/approve-parent/${userId}`);
      toast.success("Ibu bapa berjaya diluluskan!");
      fetchPendingParents();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat semasa meluluskan");
    }
  };

  const handleRejectParent = async (userId) => {
    if (!window.confirm("Adakah anda pasti mahu menolak pendaftaran ini?")) return;
    try {
      await axiosInstance.post(`/admin/reject-parent/${userId}`);
      toast.success("Pendaftaran ditolak");
      fetchPendingParents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat semasa menolak");
    }
  };

  // Initial data fetch - runs only once on mount
  // OPTIMIZED: Load critical data first (stats), then load others in background
  useEffect(() => {
    // Step 1: Load critical stats immediately (this shows the main dashboard)
    axiosInstance.get('/stats')
      .then(res => setStats(res.data))
      .catch(() => {});
    
    // Step 2: Load secondary data in background (non-blocking)
    const loadSecondaryData = async () => {
      const [billingRes, pendingRes, unreadRes, nutritionRes, teachersRes, invoicesRes] = await Promise.allSettled([
        axiosInstance.get('/billing/stats'),
        axiosInstance.get('/admin/pending-parents'),
        axiosInstance.get('/admin/notifications/unread-count'),
        axiosInstance.get('/nutrition-status/summary'),
        axiosInstance.get('/users/teachers'),
        axiosInstance.get('/billing/invoices')
      ]);
      
      if (billingRes.status === 'fulfilled') setBillingStats(billingRes.value.data);
      if (invoicesRes.status === 'fulfilled') setInvoices(invoicesRes.value.data || []);
      if (pendingRes.status === 'fulfilled') setPendingParents(pendingRes.value.data);
      if (unreadRes.status === 'fulfilled') setUnreadCount(unreadRes.value.data?.count || 0);
      if (nutritionRes.status === 'fulfilled') setNutritionalSummary(nutritionRes.value.data);
      if (teachersRes.status === 'fulfilled') setTeachers(teachersRes.value.data || []);
    };
    
    // Step 3: Load notifications last (lowest priority)
    const loadNotifications = () => {
      axiosInstance.get('/admin/notifications')
        .then(res => setNotifications(res.data || []))
        .catch(() => {});
    };
    
    // Execute with slight delays to prioritize UI responsiveness
    loadSecondaryData();
    setTimeout(loadNotifications, 500); // Load notifications after 500ms
  }, []); // Empty dependency - runs only once

  // Pull-to-refresh handler for mobile
  const handlePullRefresh = async () => {
    await Promise.all([
      fetchStats(),
      fetchBillingStats(),
      fetchPendingParents(),
      fetchNotifications(),
      fetchUnreadCount(),
      fetchNutritionalSummary(),
      fetchInvoices()
    ]);
    toast.success("Data dikemaskini!");
  };

  const handleOpenInvoiceList = () => {
    fetchInvoices();
    setShowInvoiceList(true);
  };

  const handleViewInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetail(true);
  };

  // Status invois dinormalkan: paid / pending / draft / unpaid
  const invStatus = (inv) => {
    const s = inv.status?.toLowerCase();
    return (s === 'paid' || s === 'pending' || s === 'draft') ? s : 'unpaid';
  };

  // Send WhatsApp payment reminder
  const handleSendReminder = async (invoice) => {
    if (invoice.status?.toLowerCase() === 'paid') {
      toast.error("Invoice sudah dibayar");
      return;
    }
    
    setSendingReminder(invoice.id);
    try {
      const response = await axiosInstance.post(`/billing/send-reminder/${invoice.id}`);
      toast.success(response.data.message || "Peringatan berjaya dihantar via WhatsApp");
      fetchInvoices(); // Refresh to show reminder count
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menghantar peringatan");
    } finally {
      setSendingReminder(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'draft': return 'bg-gray-100 text-gray-700';
      default: return 'bg-red-100 text-red-700';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'Dibayar';
      case 'pending': return 'Menunggu';
      case 'draft': return 'Draf';
      default: return 'Belum Bayar';
    }
  };

  const menuItems = [];

  return (
    <PageLayout user={user} onLogout={onLogout} hasAccess={hasAccess}>
      {/* Floating Background Elements */}
      <FloatingElements variant="default" />
      
      <PullToRefresh onRefresh={handlePullRefresh}>
      {/* Impersonation Banner */}
      {(isImpersonating || hasSuperadminAccess) && (
        <div className="relative z-20 bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-3 mb-4 rounded-2xl shadow-lg border border-indigo-400">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <Eye className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold">Mod Akses Cawangan</p>
                <p className="text-xs text-indigo-200">Mengakses sebagai Admin {user?.branch}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Return to Superadmin button - only show if superadmin chain exists */}
              {hasSuperadminAccess && (
                <Button
                  onClick={handleReturnToSuperadmin}
                  size="sm"
                  className="bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700 font-semibold rounded-xl shadow-lg"
                  data-testid="return-to-superadmin-btn"
                >
                  <Shield className="w-4 h-4 mr-1" />
                  Super Admin
                </Button>
              )}
              {isImpersonating && (
              <Button
                onClick={handleReturnToManager}
                size="sm"
                className="bg-white text-indigo-600 hover:bg-indigo-100 font-semibold rounded-xl"
                data-testid="return-to-manager-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Kembali
              </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto relative z-10">
        
        {/* Greeting Banner with Notification */}
        <div className="relative">
          <GreetingBanner userName={user.name} role={user.role} />
          {/* Notification Bell - positioned at top-right of banner */}
          <div className="absolute top-4 right-4 z-10">
            <AdminNotifications user={user} />
          </div>
        </div>
        
        {/* Birthday Notification Card for Admin */}
        {birthdayChildren.length > 0 && isModuleOn('birthday_notification') && (
          <BirthdayNotificationCard children={birthdayChildren} userRole={user.role} />
        )}
        
        {/* Tema Countdown Widget */}
        <TemaCountdownWidget user={user} />
        
        {/* Birthday Reminder Widget - tomorrow's birthdays */}
        <BirthdayReminderWidget user={user} />
        
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <AnimatedHeader 
            emoji="👨‍💼"
            title="Dashboard Admin"
            subtitle="💼 Urus taska anda dengan mudah"
            delay={100}
          />
          {/* Removed duplicate notification - already in GreetingBanner area */}
        </div>

        {/* Stats Cards with Animation */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 mb-8">
          <AnimatedStatCard 
            icon={Baby}
            label="Kanak-kanak"
            value={stats.total_children || 0}
            color="pink"
            delay={200}
          />
          <AnimatedStatCard 
            icon={GraduationCap}
            label="Pendidik"
            value={stats.total_teachers || 0}
            color="purple"
            delay={300}
            onClick={() => setShowTeachersModal(true)}
            className="cursor-pointer"
          />
          <AnimatedStatCard 
            icon={Users}
            label="Ibu Bapa"
            value={stats.total_parents || 0}
            color="blue"
            delay={400}
          />
          <AnimatedStatCard 
            icon={Palette}
            label="Aktiviti"
            value={stats.total_activities || 0}
            color="green"
            delay={500}
          />
          <AnimatedStatCard 
            icon={Clock}
            label="Menunggu"
            value={pendingParents.length}
            color="yellow"
            delay={600}
            onClick={() => setShowPendingModal(true)}
          />
        </div>

        {/* Billing Stats */}
        <AnimatedCard className="mb-8" delay={700} color="yellow">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-200 to-orange-200 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Status Pembayaran Invois & Bil</h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowCalendar(!showCalendar)}
                className="border-amber-300 text-amber-600 hover:bg-amber-50 rounded-xl"
                data-testid="billing-calendar-toggle-btn"
              >
                <Calendar className="w-4 h-4 mr-1" />
                {showCalendar ? 'Tutup' : 'Kalendar'}
              </Button>
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Invois & Bil</span>
            </div>
          </div>

          {/* Ringkasan Status Pembayaran */}
          <div className="grid grid-cols-3 gap-3 mb-4" data-testid="billing-status-summary">
            {[
              { key: 'paid', label: 'Dibayar', bg: 'bg-green-50 border-green-200', text: 'text-green-600' },
              { key: 'pending', label: 'Menunggu', bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-600' },
              { key: 'unpaid', label: 'Belum Bayar', bg: 'bg-red-50 border-red-200', text: 'text-red-500' },
            ].map(tile => {
              const list = invoices.filter(inv => invStatus(inv) === tile.key);
              const total = list.reduce((sum, inv) => sum + (inv.amount || 0), 0);
              return (
                <div key={tile.key} className={`rounded-2xl border-2 p-3 text-center ${tile.bg}`} data-testid={`billing-summary-${tile.key}`}>
                  <p className={`text-2xl font-black ${tile.text}`}>{list.length}</p>
                  <p className="text-xs font-bold text-gray-600">{tile.label}</p>
                  <p className={`text-[11px] font-black ${tile.text}`}>RM {total.toFixed(2)}</p>
                </div>
              );
            })}
          </div>

          {/* Calendar View */}
          {showCalendar && (
            <div className="mb-6 p-4 bg-orange-50 rounded-xl border border-orange-200">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-shrink-0">
                  <ReactCalendar
                    onChange={setSelectedDate}
                    value={selectedDate}
                    // eslint-disable-next-line react/no-unstable-nested-components
                    tileContent={({ date }) => {
                      const dateStr = getLocalDateString(date);
                      const hasInvoice = invoices.some(inv => 
                        inv.created_at && inv.created_at.startsWith(dateStr)
                      );
                      return hasInvoice ? (
                        <div className="w-2 h-2 bg-orange-500 rounded-full mx-auto mt-1"></div>
                      ) : null;
                    }}
                    className="rounded-xl border-0 shadow-lg"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-700 mb-2">
                    Invois pada {selectedDate.toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </h3>
                  {invoices.filter(inv => 
                    inv.created_at && inv.created_at.startsWith(getLocalDateString(selectedDate))
                  ).length === 0 ? (
                    <p className="text-sm text-gray-500">Tiada invois pada tarikh ini</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {invoices.filter(inv => 
                        inv.created_at && inv.created_at.startsWith(getLocalDateString(selectedDate))
                      ).map(inv => (
                        <div key={inv.id} className="p-3 bg-white rounded-lg border border-orange-200">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-sm">{inv.parent_name || inv.description || inv.invoice_number}</span>
                            <span className="font-bold text-orange-600">RM {(inv.amount || 0).toFixed(2)}</span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            inv.status?.toLowerCase() === 'paid' ? 'bg-green-100 text-green-700' :
                            inv.status?.toLowerCase() === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {inv.status?.toLowerCase() === 'paid' ? 'Dibayar' : 
                             inv.status?.toLowerCase() === 'pending' ? 'Menunggu' : 'Belum Bayar'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Status Filter */}
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-600">Pilih:</span>
              {[
                { value: 'all', label: 'Semua', color: 'gray' },
                { value: 'paid', label: 'Dibayar', color: 'green' },
                { value: 'pending', label: 'Menunggu', color: 'yellow' },
                { value: 'unpaid', label: 'Belum Bayar', color: 'red' }
              ].map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setStatusFilter(filter.value)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    statusFilter === filter.value
                      ? filter.color === 'green' ? 'bg-green-500 text-white' :
                        filter.color === 'yellow' ? 'bg-yellow-500 text-white' :
                        filter.color === 'red' ? 'bg-red-500 text-white' :
                        'bg-gray-700 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {filter.label} ({
                    filter.value === 'all' ? invoices.length :
                    invoices.filter(inv => invStatus(inv) === filter.value).length
                  })
                </button>
              ))}
            </div>
          </div>

          {/* Butang Senarai Invois */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button
              onClick={handleOpenInvoiceList}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
              data-testid="billing-view-invoices-btn"
            >
              <Receipt className="w-5 h-5 mr-2" />
              Lihat Senarai Invois & Bil
            </Button>
          </div>
        </AnimatedCard>

        {/* Nutritional Status Summary */}
        <AnimatedCard className="mb-8" delay={800} color="green">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-200 to-emerald-200 flex items-center justify-center">
              <Utensils className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Status BMI</h2>
              <p className="text-xs text-gray-500">Cawangan: {user?.branch || 'Semua'}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Jumlah */}
            <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-4 text-center text-white shadow-lg hover:scale-105 transition-transform duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 mx-auto mb-2 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <p className="text-3xl font-black">{nutritionalSummary.total_children}</p>
                <p className="text-sm font-bold text-white/90">Jumlah</p>
              </div>
            </div>
            
            {/* Normal */}
            <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl p-4 text-center text-white shadow-lg hover:scale-105 transition-transform duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 mx-auto mb-2 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <p className="text-3xl font-black">{nutritionalSummary.normal_children}</p>
                <p className="text-sm font-bold text-white/90">Normal</p>
              </div>
            </div>
            
            {/* Kekurangan Zat */}
            <div className="bg-gradient-to-br from-rose-400 to-rose-600 rounded-2xl p-4 text-center text-white shadow-lg hover:scale-105 transition-transform duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 mx-auto mb-2 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <p className="text-3xl font-black">{nutritionalSummary.malnourished_children}</p>
                <p className="text-sm font-bold text-white/90">Kekurangan Zat</p>
              </div>
            </div>
            
            {/* Tiada Data */}
            <div className="bg-gradient-to-br from-slate-400 to-slate-600 rounded-2xl p-4 text-center text-white shadow-lg hover:scale-105 transition-transform duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 mx-auto mb-2 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <p className="text-3xl font-black">{nutritionalSummary.no_data_or_out_of_range}</p>
                <p className="text-sm font-bold text-white/90">Tiada Data</p>
              </div>
            </div>
          </div>
        </AnimatedCard>

        {/* Quick Actions */}
        <AnimatedCard className="mb-8" delay={900} color="purple">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center">
              <span className="text-xl">⚡</span>
            </div>
            <h2 className="text-xl font-bold text-gray-800">Akses Pantas</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickActionButton 
              iconComponent={TeacherIcon}
              label="Pengurusan Guru"
              sublabel="Urus guru"
              color="purple"
              onClick={() => navigate('/manage-teachers')}
              delay={1000}
            />
            <QuickActionButton 
              iconComponent={ParentIcon}
              label="Pengurusan Ibu Bapa"
              sublabel="Urus ibu bapa"
              color="pink"
              onClick={() => navigate('/manage-parents')}
              delay={1100}
            />
            <QuickActionButton 
              emoji="👶"
              label="Kanak-kanak"
              sublabel="Lihat senarai"
              color="blue"
              onClick={() => navigate('/children')}
              delay={1200}
            />
            <QuickActionButton 
              emoji="✅"
              label="Kehadiran"
              sublabel="Rekod kehadiran"
              color="teal"
              onClick={() => navigate('/attendance')}
              delay={1250}
            />
            <QuickActionButton 
              emoji="📊"
              label="Laporan"
              sublabel="Jana laporan"
              color="green"
              onClick={() => navigate('/reports')}
              delay={1300}
            />
          </div>
        </AnimatedCard>

        {/* Panduan Penggunaan Card */}
        <AnimatedCard className="mb-8" delay={1000} color="blue">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Panduan Penggunaan Sistem</h2>
                  <p className="text-sm text-blue-600 font-medium">Taska Ummu Qaseh v2.0</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-4">
                Panduan lengkap untuk admin & guru menggunakan sistem Taska Ummu Qaseh. 
                Termasuk cara log masuk, rekod aktiviti, log harian, pemakanan, dan banyak lagi.
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                {[
                  { title: "Log Masuk", icon: "🔐" },
                  { title: "Aktiviti Harian", icon: "🎨" },
                  { title: "Log Harian", icon: "📋" },
                  { title: "Pemakanan", icon: "🍽️" },
                  { title: "Perkembangan", icon: "📈" },
                  { title: "Status BMI", icon: "⚖️" },
                  { title: "Tips & FAQ", icon: "💡" },
                  { title: "Dashboard", icon: "📊" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 bg-white/70 rounded-lg px-2 py-1.5">
                    <span>{item.icon}</span>
                    <span className="truncate">{item.title}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => navigate('/panduan?tab=admin')}
                  data-testid="guide-admin-button"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg"
                >
                  <BookOpen className="w-5 h-5" />
                  Panduan Admin
                </button>
                <button
                  onClick={() => navigate('/panduan?tab=guru')}
                  data-testid="guide-guru-button"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
                >
                  <BookOpen className="w-5 h-5" />
                  Panduan Guru
                </button>
              </div>
            </div>
            
            <div className="hidden md:block ml-4">
              <img 
                src="/logo-destiny.png" 
                alt="Taska Ummu Qaseh" 
                className="w-24 h-24 object-contain opacity-80 animate-float"
              />
            </div>
          </div>
        </AnimatedCard>
        
        {/* Ratio Calculator & Food Menu - MOVED TO BRANCH MANAGER DASHBOARD ONLY */}
      </div>
      </PullToRefresh>

      {/* Invoice List Dialog */}
      <Dialog open={showInvoiceList} onOpenChange={setShowInvoiceList}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden w-[95vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Receipt className="w-6 h-6 text-orange-500" />
              Senarai Invois
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full ml-2">Invois & Bil</span>
            </DialogTitle>
          </DialogHeader>
          
          {/* Invoice Filter in List */}
          <div className="mt-4 mb-4 flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-600">Pilih:</span>
            {[
              { value: 'all', label: 'Semua', color: 'gray' },
              { value: 'paid', label: 'Dibayar', color: 'green' },
              { value: 'pending', label: 'Menunggu', color: 'yellow' },
              { value: 'unpaid', label: 'Belum Bayar', color: 'red' }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  statusFilter === filter.value
                    ? filter.color === 'green' ? 'bg-green-500 text-white' :
                      filter.color === 'yellow' ? 'bg-yellow-500 text-white' :
                      filter.color === 'red' ? 'bg-red-500 text-white' :
                      'bg-gray-700 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter.label} ({
                  filter.value === 'all' ? invoices.length :
                  invoices.filter(inv => invStatus(inv) === filter.value).length
                })
              </button>
            ))}
          </div>
          
          <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2">
            {invoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Tiada invois dijumpai</p>
                <p className="text-xs text-gray-400 mt-2">Cipta invois baharu melalui menu Sistem Invois</p>
              </div>
            ) : (
              invoices
                .filter(inv => statusFilter === 'all' || invStatus(inv) === statusFilter)
                .map(invoice => (
                <div
                  key={invoice.id}
                  className="p-4 rounded-xl border-2 border-gray-100 hover:border-orange-200 bg-white shadow-sm hover:shadow-md transition-all overflow-hidden"
                >
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 w-full">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Users className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        <span className="font-semibold text-gray-800 truncate">{invoice.parent_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${getStatusColor(invoice.status)}`}>
                          {getStatusText(invoice.status)}
                        </span>
                        {invoice.is_overdue && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white flex-shrink-0">Overdue</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">{invoice.parent_email}</p>
                      {invoice.parent_phone && (
                        <p className="text-sm text-gray-500">📱 {invoice.parent_phone}</p>
                      )}
                      <p className="text-sm text-gray-700 mt-1 font-medium truncate">{invoice.invoice_number || invoice.description}</p>
                      {invoice.branch && (
                        <p className="text-xs text-blue-600 mt-1 truncate">📍 {invoice.branch}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {invoice.created_at && new Date(invoice.created_at).toLocaleDateString('ms-MY', {
                          day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="flex sm:flex-col items-center sm:items-end gap-2 w-full sm:w-auto">
                      <p className="text-xl font-bold text-gray-800">RM {(invoice.amount || 0).toFixed(2)}</p>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => handleViewInvoiceDetail(invoice)}
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        {/* WhatsApp Reminder Button */}
                        {invoice.status?.toLowerCase() !== 'paid' && (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendReminder(invoice);
                            }}
                            disabled={sendingReminder === invoice.id}
                            className="bg-green-500 hover:bg-green-600 text-white"
                            title={invoice.reminder_count > 0 ? `${invoice.reminder_count}x dihantar` : 'Hantar peringatan WhatsApp'}
                          >
                            {sendingReminder === invoice.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <MessageCircle className="w-4 h-4 mr-1" />
                                {invoice.reminder_count > 0 && (
                                  <span className="text-xs">({invoice.reminder_count})</span>
                                )}
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={showInvoiceDetail} onOpenChange={setShowInvoiceDetail}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Detail Invois
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="mt-4 space-y-4">
              {/* Invoice Header */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-orange-700">Invois #</span>
                  <span className="text-xs font-mono text-orange-600">{selectedInvoice.id.slice(0, 8).toUpperCase()}</span>
                </div>
                <p className="text-3xl font-bold text-orange-800">
                  RM {(selectedInvoice.amount || 0).toFixed(2)}
                </p>
                <span className={`text-xs px-2 py-1 rounded-full mt-2 inline-block ${getStatusColor(selectedInvoice.status)}`}>
                  {getStatusText(selectedInvoice.status)}
                </span>
              </div>

              {/* Parent Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Users className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500">Nama Ibu Bapa</p>
                    <p className="font-semibold text-gray-800">{selectedInvoice.parent_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium text-gray-700">{selectedInvoice.parent_email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Receipt className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500">Penerangan</p>
                    <p className="font-medium text-gray-700">{selectedInvoice.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Clock className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-xs text-gray-500">Tarikh Dicipta</p>
                    <p className="font-medium text-gray-700">
                      {new Date(selectedInvoice.created_at).toLocaleDateString('ms-MY', {
                        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                {selectedInvoice.due_date && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Clock className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Tarikh Tamat</p>
                      <p className="font-medium text-gray-700">
                        {new Date(selectedInvoice.due_date).toLocaleDateString('ms-MY', {
                          day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {selectedInvoice.billplz_url && (
                  <a
                    href={selectedInvoice.billplz_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-all"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Buka Billplz
                  </a>
                )}
                {selectedInvoice.invoiless_url && (
                  <a
                    href={selectedInvoice.invoiless_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-all"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Buka Invoiless
                  </a>
                )}
                <Button
                  variant="outline"
                  onClick={() => setShowInvoiceDetail(false)}
                  className="flex-1"
                >
                  Tutup
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pending Parents Modal */}
      <Dialog open={showPendingModal} onOpenChange={setShowPendingModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <Clock className="w-6 h-6" />
              Menunggu Kelulusan Ibu Bapa
            </DialogTitle>
          </DialogHeader>
          
          {pendingParents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
              <p className="text-lg font-medium">Tiada pendaftaran yang menunggu</p>
              <p className="text-sm">Semua ibu bapa telah diluluskan</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingParents.map(parent => (
                <div key={parent.id} className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-800">{parent.name}</h4>
                      <p className="text-sm text-gray-600">📱 {parent.phone || parent.whatsapp_number}</p>
                      {parent.email && <p className="text-sm text-gray-600">📧 {parent.email}</p>}
                      <p className="text-xs text-gray-500 mt-1">
                        Didaftarkan: {new Date(parent.created_at).toLocaleDateString('ms-MY', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApproveParent(parent.id)}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Luluskan
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRejectParent(parent.id)}
                        className="border-red-500 text-red-500 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Tolak
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Teachers Modal - Login sebagai Guru */}
      <Dialog open={showTeachersModal} onOpenChange={setShowTeachersModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <GraduationCap className="w-6 h-6 text-purple-500" />
              Senarai Guru
              <span className="text-sm font-normal text-gray-500">({teachers.length} guru)</span>
            </DialogTitle>
          </DialogHeader>
          
          {teachers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Tiada guru dijumpai di cawangan ini</p>
            </div>
          ) : (
            <div className="space-y-3 mt-4">
              {teachers.map((teacher) => (
                <div 
                  key={teacher.id} 
                  className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center gap-4">
                    {teacher.profile_photo ? (
                      <img 
                        src={teacher.profile_photo} 
                        alt={teacher.name}
                        className="w-14 h-14 rounded-full object-cover border-3 border-purple-200"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-bold text-xl">
                        {teacher.name?.charAt(0) || 'G'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-800 text-lg">{teacher.name}</p>
                      <p className="text-sm text-gray-500">{teacher.email}</p>
                      {teacher.assigned_classes?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {teacher.assigned_classes.slice(0, 3).map((cls, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                              📚 {cls}
                            </span>
                          ))}
                          {teacher.assigned_classes.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                              +{teacher.assigned_classes.length - 3} lagi
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => handleImpersonateTeacher(teacher.id, teacher.name)}
                      disabled={impersonatingTeacher === teacher.id}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl shadow-md px-4"
                      data-testid={`impersonate-teacher-${teacher.id}`}
                    >
                      {impersonatingTeacher === teacher.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <KeyRound className="w-4 h-4 mr-2" />
                          Masuk sebagai Guru
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NotificationToast removed - using AdminNotifications in banner instead */}
    </PageLayout>
  );
}